import React, { useState } from 'react';
import { Brodcrumb } from '../../components/ui/UtilsLayout';
import { iconsPath, imagePath, videoPath } from '../../utils/imagePath';
import { BackgroundWithCurve, CaseStudiesImageCard, CaseStudyHeading, ImpactCard, Section } from '../../utils/CaseStudiesComponents';
import VideoShimmer from '../../components/ShimmerEffects/VideoShimmer';

// CaseStudy Component
const RaghvaLifeSciences = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  return (
    <div className="flex justify-center px-5 md:px-10 lg:px-16  items-center gap-6 text-white pb-16 relative">
      <BackgroundWithCurve />
      <div className="max-w-[1200px] w-full  pt-24 md:pt-32 relative z-10">
        {/* Breadcrumb Navigation */}
        <Brodcrumb
          data={{
            name: 'Raghava Life Sciences',
            item: [
              { name: 'Industry Use Cases', path: '/all-industries' },
              {
                name: 'Healthcare & Pharma ',
                path: '/industries/healthcare-pharma',
              },
            ],
          }}
        />

        <div className="my-10 flex flex-col w-full gap-10">
          {/* Header Section with Logo */}
          <div className="flex gap-3 md:gap-5 lg:gap-10">
            <div className="flex justify-center items-center  bg-white shadow-[0px_0px_4px_1px_rgba(0,0,0,0.25)] h-20 w-32 md:h-20 md:w-20 lg:h-24 lg:w-24 rounded-lg">
              {/* Company Logo */}
              {imagePath?.RaghvaLifeLogo ? (
                <img
                  className="h-16 w-16 md:h-12 md:w-12 lg:h-16 lg:w-16 object-contain"
                  src={imagePath?.RaghvaLifeLogo}
                  alt="Raghava Life Sciences Logo"
                />
              ) : (
                <span className="text-gray-400 text-xs">No Logo</span>
              )}
            </div>

            {/* Case Study Heading */}
            <div className="flex flex-col gap-2 md:gap-5 justify-center">
              <CaseStudyHeading
                title="Raghava Life Sciences"
                desc="Immersive VR Training for manufacturing plant workers."
              />
            </div>
          </div>

          <div className="relative">
            {/* Video Container - Responsive aspect ratio */}
            <div className="relative w-full aspect-video bg-[#31333A] shadow-[0px_0px_21px_4px_rgba(0,0,0,0.25)] flex justify-center items-center ">
              {!isVideoLoaded && <VideoShimmer className="absolute inset-0" />}
              {/* Play Icon */}
              {videoPath?.RaghavaLifeVideo && (
                <video
                  src={videoPath?.RaghavaLifeVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  onLoadedData={() => setIsVideoLoaded(true)}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${
                    isVideoLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  Your browser does not support the video tag.
                </video>
              )}

              {/* {iconsPath?.QRIcon && (
                <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 lg:-top-8 lg:-right-8 bg-[#fff] p-2 md:p-3 rounded-[10px]">
                  <img
                    src={iconsPath.QRIcon}
                    alt="QR Code"
                    className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24"
                  />
                </div>
              )} */}
            </div>
          </div>

          {/* Case Study Description */}
          <Section
            title="Overview"
            content={[
              'Raghava Life Sciences partnered with Spatial Grid to modernize and scale their workforce training across their pharmaceutical manufacturing plant and R&D divisions. With complex equipment, strict GMP compliance, and high safety expectations, traditional training methods were proving slow, inconsistent, and operationally disruptive. Spatial Grid developed immersive VR training modules that allowed workers to learn critical procedures safely, accurately, and without impacting production.',
            ]}
          />

          <Section
            title="Challenges"
            content={[
              'As Raghava Life Sciences continued to scale its manufacturing and R&D operations, the organization recognized an opportunity to modernize and streamline its training processes. Traditional methods—such as classroom instruction, operator shadowing, and supervised live practice—were effective but limited by equipment availability, cleanroom access, and the need to carefully manage production schedules.',
              'Certain procedures, especially those involving specialized machinery or sensitive lab environments, could only be practiced in controlled conditions, making it difficult for new employees to gain hands-on familiarity early in their onboarding. Additionally, some scenarios, such as emergency responses or uncommon operational conditions, were not feasible to recreate in real life.',
              'Raghava Life Sciences sought an approach that would complement their existing training framework—one that offered consistent delivery, greater flexibility, and a safe, repeatable environment for operators and researchers to build confidence and competence before entering live production areas.',
            ]}
          />

          <Section
            title="Spatial Grid’s Solution"
            content={[
              'Spatial Grid created a series of high-fidelity VR training modules tailored to Raghava Life Sciences’ manufacturing workflows. These modules allowed employees to practice real-world tasks step-by-step inside a safe, simulated environment.',
            ]}
          />

          {/* Image Grid Section */}
          <div className="flex flex-col gap-3">
            <div className="flex md:flex-row flex-col gap-10 md:gap-20 w-full">
              <CaseStudiesImageCard
                image={imagePath?.RaghavaLIfeSciences1}
                title="VR Experience Interface"
              />
              <CaseStudiesImageCard
                image={imagePath?.RaghavaLIfeSciences2}
                title="VR Pharma SOP Safety Training"
              />
            </div>
          </div>

          {/* Impact Section */}
          <div className="flex flex-col gap-3">
            <Section
              title="Impact"
              content="The collaboration delivered strong, measurable improvements for Raghava Life Sciences. Onboarding for new plant and R&D personnel became 40% faster, and procedural accuracy across critical tasks increased by nearly 30%. Early training phases saw a clear reduction in safety-related incidents, while manufacturing operations benefited from zero equipment downtime since training no longer required live machinery. Standardized, trackable training records also improved audit readiness, supporting compliance efforts across departments. Overall, employees demonstrated higher confidence when working with sensitive or hazardous procedures, contributing to a safer and more efficient operational environment."
            />
          </div>
        </div>

        {/* Impact Section */}
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-5 lg:gap-10">
            {[
              {
                percentage: '+40%',
                description: 'Faster Onboarding',
              },
              {
                percentage: '+30%',
                description: 'Procedural Accuracy',
              },
              { percentage: '+0%', description: 'Equipment Downtime ' },
            ].map((impact, i) => (
              <ImpactCard
                key={i}
                percentage={impact.percentage}
                description={impact.description}
              />
            ))}
          </div>
        </div>
        <div className="relative top-8">
          <Section
            title="Conclusion"
            content={[
              'Raghava Life Sciences successfully transformed its training ecosystem using Spatial Grid’s immersive VR platform. By shifting from traditional instruction to simulation-based learning, the company improved safety, efficiency, and workforce competency across both manufacturing and R&D. This case demonstrates how Spatial Grid empowers pharma organizations to build safer, smarter, and future-ready teams through immersive technology.',
            ]}
          />
        </div>
      </div>
    </div>
  );
};
<CaseStudyHeading />;
<CaseStudiesImageCard />;

export default RaghvaLifeSciences;
